import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Plus,
  GraduationCap,
  Edit2,
  Trash2,
  Loader2,
  Search,
  User,
  Eye,
  FileText,
  Award
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GRADE_LEVELS = [
  'Kindergarten', '1st Grade', '2nd Grade', '3rd Grade', '4th Grade', '5th Grade',
  '6th Grade', '7th Grade', '8th Grade', '9th Grade', '10th Grade', '11th Grade', '12th Grade'
];

const GradesPage = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [showModal, setShowModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [formData, setFormData] = useState({ first_name: '', last_name: '', grade_level: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/grades/students`, {
        headers: getAuthHeader()
      });
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.grade_level) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSaving(true);
    
    try {
      if (editingStudent) {
        await axios.put(`${API}/grades/students/${editingStudent.id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Student updated');
      } else {
        await axios.post(`${API}/grades/students`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Student added');
      }
      
      fetchStudents();
      closeModal();
    } catch (error) {
      console.error('Error saving student:', error);
      toast.error(error.response?.data?.detail || 'Failed to save student');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (studentId) => {
    if (!confirm('Delete this student and all their grades? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/grades/students/${studentId}`, {
        headers: getAuthHeader()
      });
      setStudents(students.filter(s => s.id !== studentId));
      toast.success('Student deleted');
    } catch (error) {
      console.error('Error deleting student:', error);
      toast.error('Failed to delete student');
    }
  };

  const openAddModal = () => {
    setEditingStudent(null);
    setFormData({ first_name: '', last_name: '', grade_level: '' });
    setShowModal(true);
  };

  const openEditModal = (student) => {
    setEditingStudent(student);
    setFormData({
      first_name: student.first_name,
      last_name: student.last_name,
      grade_level: student.grade_level
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingStudent(null);
    setFormData({ first_name: '', last_name: '', grade_level: '' });
  };

  const filteredStudents = students.filter(student =>
    `${student.first_name} ${student.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.grade_level.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getGpaColor = (gpa) => {
    if (gpa >= 3.5) return 'text-green-600 bg-green-50';
    if (gpa >= 2.5) return 'text-blue-600 bg-blue-50';
    if (gpa >= 1.5) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8"
        >
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-medium text-primary mb-2">
              Grades
            </h1>
            <p className="text-muted-foreground">
              Manage student grades, report cards, and transcripts
            </p>
          </div>
          <Button
            onClick={openAddModal}
            className="gap-2 bg-primary hover:bg-primary/90"
            data-testid="add-student-btn"
          >
            <Plus className="w-4 h-4" />
            Add Student
          </Button>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search students..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12"
              data-testid="search-students"
            />
          </div>
        </motion.div>

        {/* Students Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="border-border">
                <CardContent className="p-6">
                  <Skeleton className="h-6 w-3/4 mb-3" />
                  <Skeleton className="h-4 w-1/2 mb-4" />
                  <Skeleton className="h-10 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredStudents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence>
              {filteredStudents.map((student, index) => (
                <motion.div
                  key={student.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card
                    className="border-border hover:border-primary/30 hover:shadow-lg transition-all group"
                    data-testid={`student-card-${index}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                            <User className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-medium text-primary">
                              {student.first_name} {student.last_name}
                            </h3>
                            <p className="text-sm text-muted-foreground">{student.grade_level}</p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEditModal(student)}
                            data-testid={`edit-student-${index}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => handleDelete(student.id)}
                            data-testid={`delete-student-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* GPA Display */}
                      <div className="flex items-center justify-between mb-4 p-3 bg-secondary/50 rounded-lg">
                        <span className="text-sm font-medium">Overall GPA</span>
                        <Badge className={`text-lg font-bold ${getGpaColor(student.overall_gpa)}`}>
                          {student.overall_gpa.toFixed(2)}
                        </Badge>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <Button
                          variant="default"
                          size="sm"
                          className="flex-1 gap-1"
                          onClick={() => navigate(`/grades/${student.id}`)}
                          data-testid={`view-grades-${index}`}
                        >
                          <Eye className="w-4 h-4" />
                          View Grades
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1"
                          onClick={() => navigate(`/grades/${student.id}/report-card`)}
                          data-testid={`report-card-${index}`}
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1"
                          onClick={() => navigate(`/grades/${student.id}/transcript`)}
                          data-testid={`transcript-${index}`}
                        >
                          <Award className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
              <GraduationCap className="w-10 h-10 text-muted-foreground" />
            </div>
            {searchQuery ? (
              <>
                <h3 className="font-serif text-xl font-medium text-primary mb-2">No students found</h3>
                <p className="text-muted-foreground mb-6">Try a different search term</p>
                <Button variant="outline" onClick={() => setSearchQuery('')}>
                  Clear Search
                </Button>
              </>
            ) : (
              <>
                <h3 className="font-serif text-xl font-medium text-primary mb-2">No students yet</h3>
                <p className="text-muted-foreground mb-6">Add your first student to start tracking grades</p>
                <Button onClick={openAddModal} className="gap-2 bg-primary hover:bg-primary/90">
                  <Plus className="w-4 h-4" />
                  Add Your First Student
                </Button>
              </>
            )}
          </motion.div>
        )}
      </main>

      {/* Add/Edit Student Modal */}
      <Dialog open={showModal} onOpenChange={closeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingStudent ? 'Edit Student' : 'Add Student'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="John"
                  required
                  data-testid="student-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Doe"
                  required
                  data-testid="student-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="grade_level">Grade Level</Label>
              <Select
                value={formData.grade_level}
                onValueChange={(value) => setFormData({ ...formData, grade_level: value })}
              >
                <SelectTrigger data-testid="student-grade-level">
                  <SelectValue placeholder="Select grade level" />
                </SelectTrigger>
                <SelectContent>
                  {GRADE_LEVELS.map((grade) => (
                    <SelectItem key={grade} value={grade}>
                      {grade}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-student-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingStudent ? 'Save Changes' : 'Add Student'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GradesPage;
